import React, { useEffect } from 'react';
import { Link } from 'react-router';
import { Button, Container, Icon } from 'semantic-ui-react';

import BrowserTitle from '../../utils/BrowserTitle';
import {
  completeCoursePdf,
  courseSections,
  trainingVideos,
} from './trainingContent';

import './assets/style.css';

const TrainingHome = ({ embedded = false }) => {
  useEffect(() => {
    if (window.location.hash === '#training-course-videos') {
      const courseList = document.getElementById('training-course-videos');

      if (courseList) {
        courseList.scrollIntoView();
        courseList.focus();
      }
    }
  }, []);

  return (
    <div
      className={`training-page training-home${
        embedded ? ' training-embedded' : ''
      }`}
    >
      {!embedded && <BrowserTitle title="IT-AMS New User Training" />}

      <section className="training-hero" aria-labelledby="training-title">
        <Container>
          <p className="training-eyebrow">New User Training</p>
          {embedded ? (
            <h2 id="training-title">IT-AMS New User Training</h2>
          ) : (
            <h1 id="training-title">IT-AMS New User Training</h1>
          )}
          <p className="training-hero-lead">
            Welcome to IT-AMS New User Training.
          </p>
          <p>
            This course introduces the Aligned Monitoring System and prepares
            you to complete common IT-AMS tasks based on your assigned role. The
            short videos build from basic system orientation and account access
            through navigation, review work, saving information, profile
            management, and support.
          </p>
          <p>
            If you are new to IT-AMS, complete the videos in order. After
            completing the course, return to any video whenever you need a
            refresher or guidance on a specific task.
          </p>
          <div className="training-hero-actions">
            <Button as={Link} to="/training/video/welcome" primary size="large">
              Start course <Icon name="arrow right" />
            </Button>
            <a
              className="training-all-videos-link"
              href="#training-course-videos"
            >
              View all videos
            </a>
          </div>
        </Container>
      </section>

      <Container className="training-content">
        <div className="training-summary" aria-label="Course details">
          <div>
            <span className="training-summary-label">Total Course Time</span>
            <strong>Approximately 1 hour 15 minutes</strong>
            <a
              className="training-course-pdf-link"
              href={completeCoursePdf}
              download
            >
              <Icon name="file pdf outline" aria-hidden="true" />
              Download Course PDF
            </a>
          </div>
          <div>
            <span className="training-summary-label">Course content</span>
            <strong>11 captioned videos</strong>
          </div>
          <div>
            <span className="training-summary-label">
              How to take this course
            </span>
            <strong>Watch in order or choose a topic</strong>
          </div>
        </div>

        <div
          id="training-course-videos"
          className="training-course-list"
          tabIndex="-1"
        >
          <div className="training-section-heading">
            <div>
              <p className="training-eyebrow">Course outline</p>
              <h2>Course Navigation</h2>
            </div>
            <p>Choose any topic to begin or revisit a video.</p>
          </div>

          {courseSections.map(section => (
            <section
              className="training-course-section"
              aria-labelledby={`section-${section.videoIds[0]}`}
              key={section.title}
            >
              <h3 id={`section-${section.videoIds[0]}`}>{section.title}</h3>
              <ol className="training-video-list">
                {section.videoIds.map(id => {
                  const video = trainingVideos.find(item => item.id === id);

                  return (
                    <li className="training-video-card" key={video.id}>
                      <Link
                        className="training-video-link"
                        to={`/training/video/${video.id}`}
                        aria-label={`${video.number}: ${video.title}, ${video.runtime}`}
                      >
                        <span
                          className="training-video-number"
                          aria-hidden="true"
                        >
                          {video.id === 'welcome' ? 'W' : video.id}
                        </span>
                        <span className="training-video-copy">
                          <span className="training-video-label">
                            {video.number}
                          </span>
                          <span className="training-video-title">
                            {video.title}
                          </span>
                        </span>
                        <span className="training-video-runtime">
                          <Icon name="clock outline" aria-hidden="true" />
                          <span className="sr-only">Run time: </span>
                          {video.runtime}
                        </span>
                        <Icon
                          name="chevron right"
                          className="training-video-arrow"
                          aria-hidden="true"
                        />
                      </Link>
                      <a
                        className="training-module-pdf-link"
                        href={video.pdf}
                        download
                        aria-label={`Download PDF for ${video.number}: ${video.title}`}
                      >
                        <Icon name="file pdf outline" aria-hidden="true" />
                        <span>Download PDF</span>
                      </a>
                    </li>
                  );
                })}
              </ol>
            </section>
          ))}
        </div>

        <aside className="training-help" aria-labelledby="training-help-title">
          <Icon name="question circle outline" size="big" aria-hidden="true" />
          <div>
            <h2 id="training-help-title">Need help?</h2>
            <p>
              Contact the IT-AMS Help Desk at{' '}
              <a href="tel:+18669931295">(866) 993-1295</a> or{' '}
              <a href="mailto:itamshelpdesk@acf.hhs.gov">
                itamshelpdesk@acf.hhs.gov
              </a>
              .
            </p>
          </div>
        </aside>
      </Container>
    </div>
  );
};

export default TrainingHome;
